import { ComponentPropsWithRef } from "react";
import classNames from "classnames";
import Link, { ILinkProps } from "../routing/Link";
import { Link as RouterLink } from "react-router-dom";

export type Tone = "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";
export interface ButtonAppearance {
    variant?: Tone | "link" | "input";
    outline?: boolean;
    size?: "sm" | "lg";
    fullWidth?: boolean;
}

/** Framework class names are private to the shared UI implementation. */
function buttonClasses({ variant, outline, size, fullWidth }: ButtonAppearance, className?: string): string {
    return classNames("btn", variant && `btn-${outline ? "outline-" : ""}${variant}`,
        size && `btn-${size}`, fullWidth && "d-block w-100", className);
}

export type ButtonProps = ComponentPropsWithRef<"button"> & ButtonAppearance;
export function Button({ variant, outline, size, fullWidth, className, type = "button", ...props }: ButtonProps) {
    return <button {...props} type={type} className={buttonClasses({ variant, outline, size, fullWidth }, className)} />;
}

export type ButtonLinkProps = ComponentPropsWithRef<"a"> & ButtonAppearance;
export function ButtonLink({ variant, outline, size, fullWidth, className, ...props }: ButtonLinkProps) {
    return <a {...props} className={buttonClasses({ variant, outline, size, fullWidth }, className)} />;
}

export function NavigationButtonLink({ variant, outline, size, fullWidth, className, ...props }: ILinkProps & ButtonAppearance) {
    return <Link {...props} className={buttonClasses({ variant, outline, size, fullWidth }, className)} />;
}

export function RouterButtonLink({ variant, outline, size, fullWidth, className, ...props }: ComponentPropsWithRef<typeof RouterLink> & ButtonAppearance) {
    return <RouterLink {...props} className={buttonClasses({ variant, outline, size, fullWidth }, className)} />;
}

export function CloseButton({ className, children, type = "button", "aria-label": label = "Close", ...props }: ComponentPropsWithRef<"button">) {
    return <button {...props} type={type} aria-label={label} className={classNames("tmd-close-button", className)}>
        {children ?? <span aria-hidden="true">×</span>}
    </button>;
}

export type BadgeProps = ComponentPropsWithRef<"span"> & { variant?: Tone; pill?: boolean };
export function Badge({ variant = "secondary", pill, className, ...props }: BadgeProps) {
    return <span {...props} className={classNames("badge", `tmd-badge-${variant}`, pill && "rounded-pill", className)} />;
}

export type AlertProps = ComponentPropsWithRef<"div"> & { variant?: Tone };
export function Alert({ variant = "info", className, ...props }: AlertProps) {
    return <div {...props} className={classNames("alert", `alert-${variant}`, className)} />;
}

export function ModalHeader({ className, ...props }: ComponentPropsWithRef<"div">) {
    return <div {...props} className={classNames("modal-header", className)} />;
}
export function ModalBody({ className, ...props }: ComponentPropsWithRef<"div">) {
    return <div {...props} className={classNames("modal-body", className)} />;
}
export function ModalFooter({ className, ...props }: ComponentPropsWithRef<"div">) {
    return <div {...props} className={classNames("modal-footer", className)} />;
}
